import { useEffect, useRef, useState, type JSX } from 'react'

import {
  CARD_FIELD_DISABLED_CLASS,
  CardFieldsNotReadyError,
  MAX_NAME_ON_CARD,
  NAME_ON_CARD_PROBLEM,
  cardProblem,
  type CardFieldsAdapter
} from '../lib/cardFields.ts'
import { messageFor } from '../lib/terminalApi.ts'
import type { CardDraft } from '../lib/types.ts'

export interface CardEntryProps {
  createCardFields: () => CardFieldsAdapter
  fieldCss: Record<string, unknown>
  canGoBack: boolean
  saving: boolean
  onTokenized: (draft: CardDraft) => void
  onBack: () => void
}

const TARGETS = {
  number: 'card-number-field',
  cvc: 'card-cvc-field',
  expiry: 'card-expiry-field'
}

type FieldState = 'loading' | 'ready' | 'failed'

function CardField(props: {
  id: string
  label: string
  labelId: string
  state: FieldState
  busy: boolean
  className?: string
}): JSX.Element {
  return (
    <div className={props.className ?? 'blytz-field'}>
      <span id={props.labelId} className="blytz-label">
        {props.label}
      </span>
      {/* An attribute, not a class: a className update would drop the classes VGS sets. */}
      <div
        id={props.id}
        data-testid={props.id}
        aria-labelledby={props.labelId}
        data-field-state={props.state}
        inert={props.busy}
        className="terminal-card-field"
      />
    </div>
  )
}

/**
 * Collects a new card in VGS fields and hands back its aliases to save.
 * @param props - The card field factory, field style, save state and callbacks.
 * @returns The card form.
 */
export function CardEntry(props: CardEntryProps): JSX.Element {
  const { createCardFields, fieldCss, canGoBack, saving, onTokenized, onBack } =
    props
  const adapterRef = useRef<CardFieldsAdapter | null>(null)
  // A mounted VGS field cannot be restyled, so the style at mount time is the one it keeps.
  const cssAtMount = useRef(fieldCss)
  const [kind, setKind] = useState<CardFieldsAdapter['kind'] | null>(null)
  const [tokenizing, setTokenizing] = useState(false)
  const tokenizingRef = useRef(false)
  const [problem, setProblem] = useState<string | null>(null)
  const [nameOnCard, setNameOnCard] = useState('')
  const problemRef = useRef<HTMLParagraphElement>(null)

  useEffect(() => {
    const adapter = createCardFields()
    adapterRef.current = adapter
    let live = true
    adapter.mount(TARGETS, cssAtMount.current).then(
      () => {
        if (live) setKind(adapter.kind)
      },
      (error: unknown) => {
        if (!live) return
        setProblem(
          error instanceof CardFieldsNotReadyError
            ? messageFor('unavailable')
            : 'The card form could not load. Try again later.'
        )
      }
    )
    return () => {
      live = false
      adapter.destroy()
      adapterRef.current = null
    }
  }, [createCardFields])

  useEffect(() => {
    if (problem === null) return
    problemRef.current?.scrollIntoView({ block: 'nearest' })
  }, [problem])

  const busy = tokenizing || saving
  // A className update would replace classes VGS sets on this element.
  useEffect(() => {
    for (const id of Object.values(TARGETS)) {
      document
        .getElementById(id)
        ?.classList.toggle(CARD_FIELD_DISABLED_CLASS, busy)
    }
  }, [busy])

  function onSave(): void {
    const adapter = adapterRef.current
    if (!adapter || tokenizingRef.current || saving) return
    const name = nameOnCard.trim()
    if (name === '') {
      setProblem(NAME_ON_CARD_PROBLEM)
      return
    }
    tokenizingRef.current = true
    setTokenizing(true)
    setProblem(null)
    adapter.tokenize().then(
      card => {
        tokenizingRef.current = false
        setTokenizing(false)
        onTokenized({ ...card, nameOnCard: name })
      },
      (error: unknown) => {
        tokenizingRef.current = false
        setTokenizing(false)
        setProblem(cardProblem(error))
      }
    )
  }

  const nameInvalid = problem === NAME_ON_CARD_PROBLEM
  const fieldState: FieldState =
    kind !== null ? 'ready' : problem === null ? 'loading' : 'failed'

  return (
    <section
      data-testid="card-entry"
      aria-labelledby="card-entry-heading"
      className="flex flex-col gap-3"
    >
      <h2 id="card-entry-heading" className="blytz-drawer__title">
        {'Add a card'}
      </h2>
      {kind === 'mock' && (
        <p
          data-testid="card-entry-mock"
          className="blytz-field__msg blytz-field__msg--hint"
        >
          {'Test mode: no card fields. Save card saves a test card.'}
        </p>
      )}
      <div
        data-testid="card-entry-fields"
        aria-busy={fieldState === 'loading'}
        className="flex flex-col gap-3"
      >
        <label className="blytz-field" htmlFor="card-name-field">
          <span className="blytz-label">{'Name on card'}</span>
          <input
            id="card-name-field"
            data-testid="card-name-field"
            type="text"
            autoComplete="cc-name"
            maxLength={MAX_NAME_ON_CARD}
            aria-required="true"
            aria-invalid={nameInvalid || undefined}
            aria-describedby={nameInvalid ? 'card-entry-problem' : undefined}
            className={`blytz-input${nameInvalid ? ' blytz-input--error' : ''}`}
            value={nameOnCard}
            disabled={busy}
            onChange={event => setNameOnCard(event.target.value)}
          />
        </label>
        <CardField
          id={TARGETS.number}
          label="Card number"
          labelId="card-number-label"
          state={fieldState}
          busy={busy}
        />
        <div className="flex gap-3">
          <CardField
            id={TARGETS.expiry}
            label="Expiry"
            labelId="card-expiry-label"
            state={fieldState}
            busy={busy}
            className="blytz-field min-w-0 flex-1"
          />
          <CardField
            id={TARGETS.cvc}
            label="CVV"
            labelId="card-cvc-label"
            state={fieldState}
            busy={busy}
            className="blytz-field min-w-0 flex-1"
          />
        </div>
      </div>
      {problem && (
        <p
          ref={problemRef}
          id="card-entry-problem"
          role="alert"
          data-testid="card-entry-problem"
          className="blytz-field__msg blytz-field__msg--error"
        >
          {problem}
        </p>
      )}
      <div className="blytz-modal__actions flex-col">
        <button
          type="button"
          data-testid="card-entry-save"
          className={`blytz-btn blytz-btn--primary blytz-btn--full${busy ? ' blytz-btn--loading' : ''}`}
          disabled={kind === null || busy}
          aria-busy={busy}
          onClick={onSave}
        >
          {busy ? 'Saving…' : 'Save card'}
        </button>
        {canGoBack && (
          <button
            type="button"
            data-testid="card-entry-back"
            className="blytz-btn blytz-btn--outline blytz-btn--full"
            disabled={busy}
            onClick={onBack}
          >
            {'Back'}
          </button>
        )}
      </div>
    </section>
  )
}
